import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, setDoc, query, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate, Link } from 'react-router-dom';
import './styles/admin.css';
import adminHero from '../images/image1.jpg';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FaEdit, FaCopy, FaTrash, FaBookmark } from 'react-icons/fa';

function Admin() {
  const navigate = useNavigate();
  const [kursus, setKursus] = useState({
    navn: '',
    beskrivelse: '',
    indhold: '',
    praktiskInfo: '',
    billedeUrl: '',
    slettetDato: null,
    erSlettet: false
  });
  const [kurser, setKurser] = useState([]);
  const [user, setUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [billede, setBillede] = useState(null);
  const [editingKursus, setEditingKursus] = useState(null);
  const [billedePreview, setBilledePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        hentKurser();
      } else {
        window.location.href = '/login'; // Redirect til login hvis ikke logget ind
      }
    });

    return () => unsubscribe();
  }, []);

  const hentKurser = async () => {
    const kurserCollection = collection(db, 'kurser');
    const data = await getDocs(kurserCollection);
    const alleKurser = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
    
    const aktiveKurser = alleKurser.filter(kursus => {
      if (kursus.erSlettet) {
        const slettetDato = new Date(kursus.slettetDato);
        const nuDato = new Date();
        const dageForskel = Math.floor((nuDato - slettetDato) / (1000 * 60 * 60 * 24));
        return dageForskel < 7;
      }
      return !kursus.erSlettet;
    });
    
    setKurser(aktiveKurser);
  };

  const handleEdit = (kursus) => {
    setEditingKursus(kursus);
    setKursus(kursus);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsLoading(true);
    setLoadingProgress(0);
    
    const loadingInterval = setInterval(() => {
      setLoadingProgress(prev => {
        const newProgress = prev >= 50 ? prev + 0 :
                          prev >= 25 ? prev + 0.1 :
                          prev + 0.2;
        return Math.min(newProgress, 99.9);
      });
    }, 50);

    try {
      const kursusData = {
        ...kursus,
        slug: createSlug(kursus.navn + '-' + Date.now()),
        oprettetDato: new Date().toISOString()
      };

      if (billede) {
        const billedeRef = ref(storage, `kurser/${billede.name}`);
        await uploadBytes(billedeRef, billede);
        setLoadingProgress(75);
        const billedeUrl = await getDownloadURL(billedeRef);
        setLoadingProgress(85);
        kursusData.billedeUrl = billedeUrl;
      }

      if (editingKursus) {
        const kursusRef = doc(db, 'kurser', editingKursus.id);
        await updateDoc(kursusRef, kursusData);
      } else {
        await addDoc(collection(db, 'kurser'), kursusData);
      }

      clearInterval(loadingInterval);
      setLoadingProgress(100);
      
      setTimeout(() => {
        setKursus({ navn: '', beskrivelse: '', indhold: '', praktiskInfo: '', billedeUrl: '', slettetDato: null, erSlettet: false });
        setBillede(null);
        setEditingKursus(null);
        hentKurser();
        setShowModal(false);
        setIsLoading(false);
      }, 500);

    } catch (error) {
      clearInterval(loadingInterval);
      setIsLoading(false);
      alert('Fejl: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      const eventsQuery = query(collection(db, 'events'), where('kursusId', '==', id));
      const eventsSnapshot = await getDocs(eventsQuery);
      
      const idag = new Date();
      idag.setHours(0, 0, 0, 0);
      
      for (const eventDoc of eventsSnapshot.docs) {
        const eventData = eventDoc.data();
        const eventDato = new Date(eventData.dato);
        
        if (eventDato >= idag) {
          const tilmeldingerQuery = query(
            collection(db, 'tilmeldinger'), 
            where('eventId', '==', eventDoc.id)
          );
          const tilmeldingerSnapshot = await getDocs(tilmeldingerQuery);
          
          if (tilmeldingerSnapshot.size > 0) {
            alert('Dette kursus kan ikke slettes, da der er kommende events med tilmeldinger.');
            return;
          }
        }
      }

      if (window.confirm('Er du sikker på at du vil slette dette kursus? Du har 7 dage til at fortryde.')) {
        const kursusRef = doc(db, 'kurser', id);
        await updateDoc(kursusRef, {
          erSlettet: true,
          slettetDato: new Date().toISOString()
        });
        
        hentKurser();
      }
    } catch (error) {
      alert('Fejl ved sletning: ' + error.message);
    }
  };

  const fortrydSletning = async (kursus) => {
    try {
      const kursusRef = doc(db, 'kurser', kursus.id);
      await updateDoc(kursusRef, {
        erSlettet: false,
        slettetDato: null
      });
      hentKurser();
    } catch (error) {
      alert('Fejl ved fortrydelse af sletning: ' + error.message);
    }
  };

  const handleBilledeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBillede(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBilledePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCopy = (kursusAtCopy) => {
    const kopiNavn = `${kursusAtCopy.navn}-kopi`;
    setKursus({
      ...kursusAtCopy,
      navn: kopiNavn
    });
    setShowModal(true);
    setEditingKursus(null); // Sikrer at vi opretter nyt kursus og ikke redigerer
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      ['clean']
    ],
  };

  const createSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/æ/g, 'ae')
      .replace(/ø/g, 'oe')
      .replace(/å/g, 'aa')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  if (!user) return null; // Returner intet hvis ikke logget ind

  return (
    <>
      <div className="adminmain-hero" style={{ backgroundImage: `url(${adminHero})` }}>
        <div className="adminmain-hero-overlay"></div>
        <h1>Administration</h1>
      </div>

      <div className="adminmain-container">
        <div className="admin-tools">
          <Link to="/admin/references" className="admin-tool-btn">
            <FaBookmark />
            Administrer Referencer og Cases
          </Link>
        </div>
        
        <div className="adminmain-kurser-liste">
          <h2>Kurser</h2>
          <div 
            className="adminmain-create-card"
            onClick={() => {
              setEditingKursus(null);
              setKursus({ navn: '', beskrivelse: '', indhold: '', praktiskInfo: '', billedeUrl: '', slettetDato: null, erSlettet: false });
              setShowModal(true);
            }}
          >
            <div className="adminmain-create-icon">+</div>
            <div className="adminmain-create-text">Opret nyt kursus</div>
          </div>
          {kurser.map((k) => (
            <div key={k.id} className={`adminmain-kursus-item ${k.erSlettet ? 'slettet' : ''}`}>
              {k.erSlettet && (
                <div className="adminmain-slettet-banner">
                  <p>Dette kursus bliver slettet om {7 - Math.floor((new Date() - new Date(k.slettetDato)) / (1000 * 60 * 60 * 24))} dage</p>
                  <button onClick={() => fortrydSletning(k)} className="adminmain-fortryd-btn">
                    Fortryd Sletning
                  </button>
                </div>
              )}
              <img src={k.billedeUrl || adminHero} alt={k.navn} />
              <div className="adminmain-kursus-content">
                <h3>{k.navn}</h3>
                <p>{k.beskrivelse}</p>
              </div>
              <div className="adminmain-kursus-actions">
                <button 
                  onClick={() => handleEdit(k)} 
                  className="adminmain-edit-btn"
                  title="Rediger"
                >
                  <FaEdit />
                </button>
                <button 
                  onClick={() => handleCopy(k)} 
                  className="adminmain-copy-btn"
                  title="Kopier"
                >
                  <FaCopy />
                </button>
                <button 
                  onClick={() => handleDelete(k.id)}
                  className="adminmain-delete-btn"
                  title="Slet"
                >
                  <FaTrash />
                </button>
                <button 
                  onClick={() => navigate(`/admin/${k.id}-events`)}
                  className="adminmain-events-btn"
                >
                  Events
                </button>
                <button 
                  onClick={() => navigate(`/admin/${k.id}/tilmeldinger`)}
                  className="adminmain-tilmeldinger-btn"
                >
                  Tilmeldinger
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showModal && (
        <div className="adminmain-modal">
          <div className="adminmain-modal-content">
            <span className="adminmain-close" onClick={() => setShowModal(false)}>&times;</span>
            <h2>{editingKursus ? 'Rediger Kursus' : 'Opret Nyt Kursus'}</h2>
            
            {isLoading && (
              <>
                <div className="adminmain-loading-overlay"></div>
                <div className="adminmain-loading-container">
                  <div className="adminmain-loading-bar">
                    <div 
                      className="adminmain-loading-progress" 
                      style={{ width: `${loadingProgress}%` }}
                    ></div>
                  </div>
                  <div className="adminmain-loading-text">
                    {loadingProgress.toFixed(0)}%
                  </div>
                </div>
              </>
            )}

            <form onSubmit={handleSubmit}>
              <div className="adminmain-form-group">
                <label>Navn:</label>
                <input
                  type="text"
                  value={kursus.navn}
                  onChange={(e) => setKursus({...kursus, navn: e.target.value})}
                  required
                />
              </div>

              <div className="adminmain-form-group">
                <label>Beskrivelse:</label>
                <textarea
                  value={kursus.beskrivelse}
                  onChange={(e) => setKursus({...kursus, beskrivelse: e.target.value})}
                  required
                />
              </div>

              <div className="adminmain-form-group">
                <label>Indhold:</label>
                <ReactQuill
                  value={kursus.indhold}
                  onChange={(value) => setKursus({...kursus, indhold: value})}
                  modules={modules}
                />
              </div>

              <div className="adminmain-form-group">
                <label>Praktisk Information:</label>
                <ReactQuill
                  value={kursus.praktiskInfo}
                  onChange={(value) => setKursus({...kursus, praktiskInfo: value})}
                  modules={modules}
                />
              </div>

              <div className="adminmain-form-group">
                <label>Billede:</label>
                <div className="adminmain-image-upload">
                  <label htmlFor="billede-input">
                    <div className="adminmain-image-preview">
                      {(billedePreview || editingKursus?.billedeUrl) && (
                        <img 
                          src={billedePreview || editingKursus?.billedeUrl} 
                          alt="Preview" 
                        />
                      )}
                      {!billedePreview && !editingKursus?.billedeUrl && (
                        <div className="adminmain-image-placeholder">
                          <span>+</span>
                          <p>Vælg billede</p>
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    id="billede-input"
                    type="file"
                    onChange={handleBilledeChange}
                    accept="image/*"
                    className="adminmain-image-input"
                  />
                </div>
              </div>

              <button type="submit" className="adminmain-submit-btn">
                {editingKursus ? 'Opdater' : 'Opret'} Kursus
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default Admin;